import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Download,
  Eye,
  MoreVertical,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  FileText,
  Cpu,
  Package,
  Settings,
  Code2,
  Award,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  Clock,
  XCircle,
  Layers,
} from 'lucide-react';

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const VERIFICATION_TYPE_STYLES = {
  Document: { icon: FileText, cls: 'bg-rose-50 text-rose-600' },
  Binary: { icon: Cpu, cls: 'bg-sky-50 text-sky-600' },
  ZIP: { icon: Package, cls: 'bg-violet-50 text-violet-600' },
  Configuration: { icon: Settings, cls: 'bg-emerald-50 text-emerald-600' },
  Code: { icon: Code2, cls: 'bg-orange-50 text-orange-600' },
  Certificate: { icon: Award, cls: 'bg-amber-50 text-amber-600' },
};

const summaryCards = [
  { title: 'Total Verifications', value: '248', change: '18% vs. last month', Icon: ShieldCheck, positive: true },
  { title: 'Successful', value: '236', change: '21% vs. last month', Icon: CheckCircle2, positive: true },
  { title: 'Pending', value: '8', change: '3% vs. last month', Icon: Clock, positive: true },
  { title: 'Failed', value: '4', change: '2% vs. last month', Icon: XCircle, positive: false },
];

const VERIFICATION_TYPES = ['All Verification Types', 'Document', 'Binary', 'Configuration', 'Code', 'Certificate', 'ZIP'];
const STATUSES = ['All Statuses', 'Verified', 'Pending', 'Failed'];
const PAGE_SIZE = 10;

const baseVerifications = [
  {
    id: '1', fileName: 'contract_agreement.pdf', size: '1.2 MB', type: 'Document',
    blockchain: 'Ethereum', blockchainDisplay: 'Ethereum (Public)',
    txIdShort: '0x7f3a9c4e2b6d...8e9f', txIdFull: '0x7f3a9c4e2b6d8e9f4a1c2b3d4e5f6a7b8c9d0e1f',
    verifiedOn: 'May 31, 2025 10:42 AM', status: 'Verified',
    uploadedBy: 'Priya Nair (priya.nair@acme.com)', organization: 'Acme Corp',
    tags: ['contract', 'legal', 'agreement'], verificationType: 'Document Signature',
    blockNumber: '7,842,551', cidFull: 'QmXn7e9g8JbW2n1r4Zk9v6b7Q3h5T8a1pZL4m6NBo09pQr',
  },
  {
    id: '2', fileName: 'firmware_v2.1.bin', size: '8.8 MB', type: 'Binary',
    blockchain: 'IPFS', blockchainDisplay: 'IPFS Public Network',
    txIdShort: 'QmTF8v3h9kL2p6z1...', txIdFull: 'QmTF8v3h9kL2p6z1Rm4Xc8Yw3Nq5Ub7Ea9Dj2Gs6Hf1Kt',
    verifiedOn: 'May 30, 2025 04:15 PM', status: 'Verified',
    uploadedBy: 'DevOps Bot', organization: 'Acme Corp',
    tags: ['firmware', 'iot', 'binary'], verificationType: 'Binary Hash Check',
    blockNumber: '7,841,200', cidFull: 'QmTF8v3h9kL2p6z1Rm4Xc8Yw3Nq5Ub7Ea9Dj2Gs6Hf1Kt',
  },
  {
    id: '3', fileName: 'deployment_package.zip', size: '15.6 MB', type: 'ZIP',
    blockchain: 'Ethereum', blockchainDisplay: 'Ethereum (Public)',
    txIdShort: '0x5c7e2d9f1b3a...4c6d', txIdFull: '0x5c7e2d9f1b3a4c6d5e6f7a8b9c0d1e2f3a4b5c6d',
    verifiedOn: 'May 30, 2025 04:32 PM', status: 'Verified',
    uploadedBy: 'Release Manager', organization: 'Acme Corp',
    tags: ['deploy', 'package'], verificationType: 'Archive Check',
    blockNumber: '7,841,310', cidFull: 'QmZ8k6p9w1n4t7e2Qa5Sd8Fg3Hj6Kl9Zx2Cv5Bn8Mq1Wr',
  },
  {
    id: '4', fileName: 'k8s_manifest.yaml', size: '12 KB', type: 'Configuration',
    blockchain: 'IPFS', blockchainDisplay: 'IPFS Public Network',
    txIdShort: 'QmN1z4g8t3h9e2...', txIdFull: 'QmN1z4g8t3h9e2Pk5Lm8Jn3Hb6Vc9Xz2Aq5Sw8Ed1Rf',
    verifiedOn: 'May 28, 2025 03:21 PM', status: 'Verified',
    uploadedBy: 'K8s Operator', organization: 'Acme Corp',
    tags: ['k8s', 'yaml'], verificationType: 'Config Schema Validation',
    blockNumber: '7,830,450', cidFull: 'QmN1z4g8t3h9e2Pk5Lm8Jn3Hb6Vc9Xz2Aq5Sw8Ed1Rf',
  },
  {
    id: '5', fileName: 'report_final.pdf', size: '2.4 MB', type: 'Document',
    blockchain: 'Ethereum', blockchainDisplay: 'Ethereum (Public)',
    txIdShort: '0x8e4c7d2a9f1b...6c3e', txIdFull: '0x8e4c7d2a9f1b3c5e7a9b0c1d2e3f4a5b6c7d8e9f',
    verifiedOn: 'May 27, 2025 09:12 AM', status: 'Verified',
    uploadedBy: 'Audit Lead', organization: 'Acme Corp',
    tags: ['report', 'audit'], verificationType: 'Document Signature',
    blockNumber: '7,824,110', cidFull: 'QmR7t3n9p5v1k2o6Fg2Hj5Kl8Zx1Cv4Bn7Mq9Ws3Ed6Rt2',
  },
  {
    id: '6', fileName: 'iot_device_firmware.bin', size: '4.3 MB', type: 'Binary',
    blockchain: 'IPFS', blockchainDisplay: 'IPFS Public Network',
    txIdShort: 'QmF6p1t8v9z3k2m7...', txIdFull: 'QmF6p1t8v9z3k2m7Ns2Bv5Cx8Zl1Km4Jh7Gf9Ds3Aq6Wr',
    verifiedOn: 'May 26, 2025 02:45 PM', status: 'Verified',
    uploadedBy: 'IoT Team', organization: 'Acme Corp',
    tags: ['iot', 'firmware'], verificationType: 'Binary Hash Check',
    blockNumber: '7,818,900', cidFull: 'QmF6p1t8v9z3k2m7Ns2Bv5Cx8Zl1Km4Jh7Gf9Ds3Aq6Wr',
  },
  {
    id: '7', fileName: 'source_code.tar.gz', size: '22.7 MB', type: 'Code',
    blockchain: 'Ethereum', blockchainDisplay: 'Ethereum (Public)',
    txIdShort: '0x4d7a9c3e2b6f...8c1d', txIdFull: '0x4d7a9c3e2b6f1a8c2b3d4e5f6a7b8c9d0e1f2a3b',
    verifiedOn: 'May 25, 2025 11:30 AM', status: 'Failed',
    uploadedBy: 'Lead Dev', organization: 'Acme Corp',
    tags: ['source', 'code'], verificationType: 'Signature Mismatch',
    blockNumber: '7,812,400', cidFull: 'QmP9k2v7n5t4e8z1Hd3Fg6Jk9Lq2Zx5Cv8Bn1Mw4Er7Ty',
  },
  {
    id: '8', fileName: 'policy_document.pdf', size: '980 KB', type: 'Document',
    blockchain: 'IPFS', blockchainDisplay: 'IPFS Public Network',
    txIdShort: 'QmT5r8p3k1n6v9e2...', txIdFull: 'QmT5r8p3k1n6v9e2Pd7Fs3Gh9Jk2Lz5Xc8Vb1Nm4Qw6Er',
    verifiedOn: 'May 24, 2025 04:10 PM', status: 'Verified',
    uploadedBy: 'Compliance Officer', organization: 'Acme Corp',
    tags: ['policy'], verificationType: 'Document Signature',
    blockNumber: '7,805,120', cidFull: 'QmT5r8p3k1n6v9e2Pd7Fs3Gh9Jk2Lz5Xc8Vb1Nm4Qw6Er',
  },
  {
    id: '9', fileName: 'certificate.crt', size: '6.7 KB', type: 'Certificate',
    blockchain: 'Ethereum', blockchainDisplay: 'Ethereum (Public)',
    txIdShort: '0x7c3e9a1d5f2b...6e7a', txIdFull: '0x7c3e9a1d5f2b4c8e1a2b3c4d5e6f7a8b9c0d1e2f',
    verifiedOn: 'May 25, 2025 01:25 PM', status: 'Pending',
    uploadedBy: 'Security Admin', organization: 'Acme Corp',
    tags: ['cert', 'ssl'], verificationType: 'Certificate Validation',
    blockNumber: '7,813,000', cidFull: 'QmV6n3t9p4k8z1e2Rd4Fg7Hj9Kl3Zx6Cv1Bn5Mq8Ws2Er',
  },
  {
    id: '10', fileName: 'application_build.zip', size: '18.4 MB', type: 'ZIP',
    blockchain: 'IPFS', blockchainDisplay: 'IPFS Public Network',
    txIdShort: 'QmJ8p2v6t9n4k3e1...', txIdFull: 'QmJ8p2v6t9n4k3e1Sd5Fg8Hj2Kl6Zx9Cv3Bn7Mq1Wr4Et',
    verifiedOn: 'May 24, 2025 10:18 AM', status: 'Verified',
    uploadedBy: 'CI/CD Pipeline', organization: 'Acme Corp',
    tags: ['build', 'zip'], verificationType: 'Archive Check',
    blockNumber: '7,802,890', cidFull: 'QmJ8p2v6t9n4k3e1Sd5Fg8Hj2Kl6Zx9Cv3Bn7Mq1Wr4Et',
  },
];

export default function VerificationsPage() {
  const navigate = useNavigate();

  const [activeTab] = useState('verifications');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Verification Types');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [selectedRecord, setSelectedRecord] = useState(baseVerifications[0]);
  const [page, setPage] = useState(1);
  const [selectedSideTab, setSelectedSideTab] = useState('Overview');

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return baseVerifications.filter((item) => {
      const matchesSearch =
        !query ||
        item.fileName.toLowerCase().includes(query) ||
        item.txIdFull.toLowerCase().includes(query) ||
        item.uploadedBy.toLowerCase().includes(query);
      const matchesType = typeFilter === 'All Verification Types' || item.type === typeFilter;
      const matchesStatus = statusFilter === 'All Statuses' || item.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [search, typeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleRecords = filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstRecord = filteredRecords.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastRecord = Math.min(currentPage * PAGE_SIZE, filteredRecords.length);
  const pageButtons = totalPages > 1 ? [1, 2, 3, 4, 5, '...', 25] : [1];

  const updateFilter = (setter, value) => { setter(value); setPage(1); };
  const handlePageChange = (nextPage) => setPage(Math.max(1, Math.min(nextPage, totalPages)));

  return (
    <div className="min-h-screen bg-[#f7fbff] text-[#1e3559]">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;
              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-black hover:text-black'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        {/* Summary Cards - separated individual bordered cards matching other pages */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.Icon;
            return (
              <div
                key={card.title}
                className="min-w-0 rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                    <Icon className="h-7 w-7 text-[#6426df]" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">{card.title}</p>
                    <p className="text-xl font-bold leading-tight text-[#20375e]">{card.value}</p>
                    <p className={`text-[9px] leading-tight ${card.positive ? 'text-[#21a675]' : 'text-[#e5484d]'}`}>
                      {card.positive ? <ArrowUp className="h-3 w-3 inline" strokeWidth={2} /> : <ArrowDown className="h-3 w-3 inline" strokeWidth={2} />}
                      {card.change}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Table + Right Details Sidebar Layout */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_240px] xl:grid-cols-[minmax(0,1fr)_255px]">
          {/* Table Card */}
          <div className="min-w-0 overflow-hidden rounded-md border border-[#e5edf7] bg-white shadow-sm">
            {/* Filters Bar */}
            <div className="flex items-center gap-2 border-b border-[#edf2f8] bg-[#f8fbff] px-3 py-2.5">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#86a0be]" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => updateFilter(setSearch, e.target.value)}
                  placeholder="Search by file name, transaction ID, hash, or issuer..."
                  className="h-7 w-full rounded border border-[#dfe9f4] bg-white pl-7 pr-2 text-[10px] text-[#49678f] outline-none placeholder:text-[#91a5bf] focus:border-[#8d62ea]"
                />
              </div>

              <div className="relative shrink-0">
                <select value={typeFilter} onChange={(e) => updateFilter(setTypeFilter, e.target.value)} className="h-7 appearance-none rounded border border-[#dfe9f4] bg-white pl-2.5 pr-6 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {VERIFICATION_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>

              <div className="relative shrink-0">
                <select value={statusFilter} onChange={(e) => updateFilter(setStatusFilter, e.target.value)} className="h-7 appearance-none rounded border border-[#dfe9f4] bg-white pl-2.5 pr-6 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>

              <button type="button" className="flex h-7 shrink-0 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2.5 text-[10px] text-[#5b769c]">
                <Calendar className="h-3 w-3 text-[#718daf]" />
                May 1, 2025 – May 31, 2025
                <ChevronDown className="h-2.5 w-2.5 text-[#87a0be]" />
              </button>
              <button type="button" className="flex h-7 shrink-0 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2.5 text-[10px] font-medium text-[#6426df] hover:bg-violet-50">
                <Download className="h-3 w-3" />
                Export
              </button>
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-[32px]" />
                  <col className="w-[36px]" />
                  <col />
                  <col className="w-[100px]" />
                  <col className="w-[90px]" />
                  <col className="w-[120px]" />
                  <col className="w-[100px]" />
                  <col className="w-[70px]" />
                  <col className="w-[60px]" />
                </colgroup>
                <thead>
                  <tr className="h-9 border-b border-[#edf2f8] bg-[#f8fbff] text-left text-[10px] font-semibold uppercase tracking-wide text-[#7791b3]">
                    <th className="w-[38px] px-2 text-center">
                      <input type="checkbox" className="rounded border-[#cbd5e1]" aria-label="Select all rows" />
                    </th>
                    <th className="w-[44px] px-2 text-center">#</th>
                    <th className="px-2">File / Document</th>
                    <th className="px-2">Verification Type</th>
                    <th className="px-2">Blockchain / IPFS</th>
                    <th className="px-2">Transaction ID</th>
                    <th className="px-2">Verified On</th>
                    <th className="px-2">Status</th>
                    <th className="w-[75px] px-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRecords.map((item, index) => {
                    const typeStyle = VERIFICATION_TYPE_STYLES[item.type] || VERIFICATION_TYPE_STYLES.Document;
                    const TypeIcon = typeStyle.icon;
                    const isSelected = selectedRecord?.id === item.id;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => setSelectedRecord(item)}
                        className={`h-[52px] cursor-pointer border-b border-[#f0f4f8] transition hover:bg-[#f9fbff] ${isSelected ? 'bg-[#fbfaff]' : 'bg-white'}`}
                      >
                        <td className="w-[38px] px-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="rounded border-[#cbd5e1]" aria-label={`Select row ${item.fileName}`} />
                        </td>
                        <td className="w-[44px] px-2 text-center text-xs font-medium text-[#58749c]">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td className="px-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded ${typeStyle.cls}`}>
                              <TypeIcon className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-medium text-[#46658e]" title={item.fileName}>{item.fileName}</p>
                              <p className="truncate text-[10px] text-[#8098b6]">{item.size}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2">
                          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${typeStyle.cls}`}>
                            <TypeIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{item.type}</span>
                          </span>
                        </td>
                        <td className="px-2">
                          <div className="flex items-center gap-1.5 text-xs text-[#56729a]">
                            <Layers className="h-3.5 w-3.5 text-[#6426df]" />
                            <span className="truncate">{item.blockchain}</span>
                          </div>
                        </td>
                        <td className="px-2">
                          <p title={item.txIdFull} className="truncate font-mono text-[11px] text-[#6f88a8]">{item.txIdShort}</p>
                        </td>
                        <td className="px-2">
                          <p className="text-xs leading-4 text-[#56729a]">{item.verifiedOn}</p>
                        </td>
                        <td className="px-2">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="w-[75px] px-2">
                          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => setSelectedRecord(item)}
                              title="View record details"
                              className="text-[#7c9fca] transition hover:text-[#6426df]"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              title="More actions"
                              className="text-[#8ca4c2] transition hover:text-[#6426df]"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleRecords.length === 0 && (
                    <tr>
                      <td colSpan={9} className="h-72 text-center text-xs text-[#8ca2bd]">No verification records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-t border-[#edf2f8] px-4 py-3">
              <p className="text-[10px] text-[#6682aa]">
                Showing {firstRecord}-{lastRecord} of {filteredRecords.length} records
              </p>
              <nav aria-label="Verifications table pagination" className="flex items-center gap-1">
                <PaginationButton disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </PaginationButton>
                {pageButtons.map((item) =>
                  item === '...' ? (
                    <span key="ellipsis" className="flex h-7 w-7 items-center justify-center rounded border border-[#e2eaf4] text-[10px] text-[#7690b2]">...</span>
                  ) : (
                    <PaginationButton key={item} active={currentPage === item} onClick={() => handlePageChange(item)} label={`Page ${item}`}>
                      {item}
                    </PaginationButton>
                  )
                )}
                <PaginationButton disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </PaginationButton>
              </nav>
            </div>
          </div>

          {/* Right-Side Verification Details Sidebar */}
          {selectedRecord && (
            <VerificationDetails
              record={selectedRecord}
              selectedSideTab={selectedSideTab}
              setSelectedSideTab={setSelectedSideTab}
              onClose={() => setSelectedRecord(null)}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function PaginationButton({ children, onClick, disabled = false, active = false, label }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex h-7 w-7 items-center justify-center rounded border text-[10px] font-medium transition ${
        active
          ? 'border-[#6200ee] bg-[#6200ee] text-white'
          : 'border-[#e2eaf4] bg-white text-[#718caf] hover:bg-[#f7faff]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Verified: 'bg-[#dff7ea] text-[#29996e]',
    Pending: 'bg-amber-50 text-amber-600',
    Failed: 'bg-rose-50 text-rose-600',
  };
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-[9px] font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

function DetailRow({ label, value, children, link = false, compact = false }) {
  return (
    <div className="grid grid-cols-[100px_minmax(0,1fr)] items-start gap-2">
      <span className="text-[11px] leading-[15px] text-[#8198b7]">{label}</span>
      {children ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="flex min-w-0 items-start gap-1">
          <span
            title={value}
            className={`min-w-0 break-all leading-[15px] ${compact ? 'font-mono text-[10px]' : 'text-[11px]'} ${link ? 'text-[#6426df]' : 'text-[#55719a]'}`}
          >
            {value}
          </span>
          {link && <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[#6426df]" />}
        </div>
      )}
    </div>
  );
}

function VerificationDetails({ record, selectedSideTab, setSelectedSideTab, onClose }) {
  const typeStyle = VERIFICATION_TYPE_STYLES[record.type] || VERIFICATION_TYPE_STYLES.Document;
  const FileTypeIcon = typeStyle.icon;
  const ipfsGatewayUrl = `https://ipfs.io/ipfs/${record.cidFull}`;

  return (
    <aside className="min-w-0 rounded-md border border-[#e5edf7] bg-white p-2.5 shadow-sm">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[11px] font-bold text-[#29446c]">Verification Details</h2>
        <StatusBadge status={record.status} />
      </div>

      {/* File Identity Header */}
      <div className="mb-2.5 flex items-center gap-2">
        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${typeStyle.cls}`}>
          <FileTypeIcon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <h3 title={record.fileName} className="truncate text-[11px] font-semibold text-[#29446c]">{record.fileName}</h3>
          <p className="mt-0.5 text-[10px] text-[#8299b8]">{record.size}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="mb-2.5 flex border-b border-[#eaf0f7]">
        {['Overview', 'IPFS Details', 'Blockchain'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setSelectedSideTab(tab)}
            className={`relative flex-1 pb-2 text-center text-[10px] font-medium transition ${
              selectedSideTab === tab ? 'text-[#6426df]' : 'text-[#8198b7] hover:text-[#29446c]'
            }`}
          >
            {tab}
            {selectedSideTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#6426df]" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {selectedSideTab === 'Overview' && (
        <div className="space-y-2.5">
          {/* File Information */}
          <div>
            <h4 className="mb-1.5 text-[10px] font-semibold text-[#29446c]">File Information</h4>
            <div className="space-y-1.5">
              <DetailRow label="File Name" value={record.fileName} />
              <DetailRow label="File Type" value={record.type} />
              <DetailRow label="Size" value={record.size} />
              <DetailRow label="Uploaded By" value={record.uploadedBy} />
              <DetailRow label="Uploaded On" value={record.verifiedOn} />
              <DetailRow label="Organization" value={record.organization} />
              <DetailRow label="Tags">
                <div className="flex flex-wrap gap-1">
                  {record.tags.map((tag) => (
                    <span key={tag} className="rounded bg-[#f0f4f8] px-1.5 py-0.5 text-[10px] text-[#55719a]">
                      {tag}
                    </span>
                  ))}
                </div>
              </DetailRow>
            </div>
          </div>

          {/* Verification Information */}
          <div className="border-t border-[#eaf0f7] pt-2">
            <h4 className="mb-1.5 text-[10px] font-semibold text-[#29446c]">Verification Information</h4>
            <div className="space-y-1.5">
              <div className="grid grid-cols-[100px_minmax(0,1fr)] items-center gap-2">
                <span className="text-[11px] text-[#8198b7]">Status</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#29996e]">
                  <CheckCircle2 className="h-3 w-3 text-[#29996e]" /> Verified
                </span>
              </div>
              <DetailRow label="Verification Type" value={record.verificationType} />
              <DetailRow label="Transaction ID" value={record.txIdFull} link compact />
              <DetailRow label="Blockchain" value={record.blockchainDisplay} />
              <DetailRow label="IPFS CID" value={record.cidFull} link compact />
              <DetailRow label="Explorer Link">
                <button type="button" className="inline-flex items-center gap-1 text-[11px] text-[#6426df] hover:underline">
                  View on Etherscan <ExternalLink className="h-2.5 w-2.5" />
                </button>
              </DetailRow>
            </div>
          </div>

          {/* Blockchain Record Banner/Card */}
          <div className="border-t border-[#eaf0f7] pt-2">
            <h4 className="mb-1.5 text-[10px] font-semibold text-[#29446c]">Blockchain Record</h4>
            <div className="rounded border border-[#dff7ea] bg-[#f4fbf7] p-2 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#29996e]">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Record found and verified on Ethereum
              </div>
              <DetailRow label="Block Number" value={record.blockNumber} />
              <DetailRow label="Transaction Hash" value={record.txIdFull} compact link />
              <DetailRow label="Timestamp" value={record.verifiedOn} />
            </div>
          </div>

          {/* IPFS Details Banner/Card */}
          <div className="border-t border-[#eaf0f7] pt-2">
            <h4 className="mb-1.5 text-[10px] font-semibold text-[#29446c]">IPFS Details</h4>
            <div className="rounded border border-[#dff7ea] bg-[#f4fbf7] p-2 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#29996e]">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                File available on IPFS
              </div>
              <DetailRow label="CID" value={record.cidFull} compact link />
              <DetailRow label="Gateway URL" value={ipfsGatewayUrl} compact link />
              <div className="pt-1">
                <button
                  type="button"
                  className="flex h-7 w-full items-center justify-center gap-1 rounded border border-[#a987ed] bg-white text-xs font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
                >
                  View on IPFS <ExternalLink className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSideTab === 'IPFS Details' && (
        <div className="space-y-1.5 pt-1">
          <DetailRow label="IPFS Network" value="Public Mainnet" />
          <DetailRow label="Storage Node" value="ipfs-node-04.acme.internal" />
          <DetailRow label="Replication Factor" value="3 nodes pinned" />
          <DetailRow label="Pin Status" value="Pinned & Active" />
          <DetailRow label="Availability" value="100.0%" />
          <DetailRow label="Full CID" value={record.cidFull} compact link />
          <DetailRow label="Gateway Link" value={ipfsGatewayUrl} compact link />
        </div>
      )}

      {selectedSideTab === 'Blockchain' && (
        <div className="space-y-1.5 pt-1">
          <DetailRow label="Consensus" value="Proof of Stake" />
          <DetailRow label="Gas Used" value="21,450 gas" />
          <DetailRow label="Smart Contract" value="0x1A2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b" compact link />
          <DetailRow label="Block Hash" value="0x9f8e7d6c5b4a3f2e1d0c...b5a4" compact link />
          <DetailRow label="Confirmations" value="1,248 blocks" />
          <DetailRow label="Timestamp" value={record.verifiedOn} />
        </div>
      )}
    </aside>
  );
}